import { useEffect, useMemo, useState } from 'react';
import { Settings2, Spline } from 'lucide-react';
import { WorkflowBlock, BlockConfig, Connector, BlockGroup, BlockDimensions, WorkflowSheet, CrossSheetConnector } from '@/types/workflow';
import { ConnectorPropertiesSection } from './ConnectorPropertiesSection';
import { TemplateSettingsSection } from './properties/TemplateSettingsSection';
import { GroupPropertiesSection } from './properties/GroupPropertiesSection';
import { BlockPropertiesSection } from './properties/BlockPropertiesSection';
import { OverviewSection } from './properties/OverviewSection';
import { CrossSheetLinksSection } from './properties/CrossSheetLinksSection';
import { BlockNameSuggestions } from './properties/BlockNameSuggestions';
import { getAvailableReturnPortPair } from '@/lib/connectorUtils';
import { getUsedPortKeys } from '@/lib/connectorPortRules';
import { useMinimapCapabilities } from '@/hooks/useMinimapCapabilities';
import { useMinimapSearchProps } from '@/hooks/useMinimapSearchProps';
import { usePeerCursors } from '@/hooks/usePeerCursors';
import type { MinimapMode } from '@/types/minimap';
import { useTranslation } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

interface PropertiesPanelProps {
  selectedBlock: WorkflowBlock | null;
  selectedGroup: BlockGroup | null;
  /** When set (and no block/group is selected), the connector with this id
   *  is shown for editing instead. Mutually exclusive with the other two. */
  selectedConnectorId?: string | null;
  workflowName: string;
  onUpdateWorkflowName: (name: string) => void;
  onUpdateBlock: (id: string, updates: Partial<WorkflowBlock>) => void;
  onDeleteBlock: (id: string) => void;
  onUpdateGroup: (id: string, updates: Partial<BlockGroup>) => void;
  onDeleteGroup: (id: string) => void;
  /** Connector mutators — only required when selectedConnectorId can be set. */
  onUpdateConnector?: (id: string, patch: Partial<Connector>) => void;
  onDeleteConnector?: (id: string) => void;
  onUpdateConnectorWaypoints?: (id: string, waypoints: { x: number; y: number }[]) => void;
  onCreateConnector?: (
    fromId: string, toId: string,
    fromPort?: import('@/types/workflow').PortSide,
    toPort?: import('@/types/workflow').PortSide,
  ) => void;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  blockDimensions: Record<string, BlockDimensions>;
  canvasWidth: number;
  canvasHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  zoom: number;
  onMinimapNavigate: (x: number, y: number) => void;
  onNavigateToGroup?: (groupId: string | null) => void;
  // Cross-sheet linking — all optional so this prop-set is backwards
  // compatible. When provided, a Cross-sheet links section renders at the
  // bottom of a block's properties with: existing links for this block and
  // a "Link to adjacent sheet" button that starts the mode with this block
  // pre-picked as source.
  sheets?: WorkflowSheet[];
  activeSheetIndex?: number;
  crossSheetConnectors?: CrossSheetConnector[];
  onStartCrossSheetLink?: (blockId: string) => void;
  onDeleteCrossSheetLink?: (id: string) => void;
  onNavigateToSheet?: (index: number) => void;
  /**
   * Workflow id for live-filter integration. When supplied the
   * minimap subscribes to LiveFilterContext and highlights blocks
   * matched by the current Search Canvas query.
   */
  workflowId?: string | null;
}

/**
 * Right-hand properties side panel.
 *
 * Commit pattern (shared with `BlockEditDialog`):
 *   • text fields (block name, description, workflow name) → local
 *     state (`localConfig`, `localWorkflowName`). Commits on explicit
 *     Save button press (block/group) or on blur / Enter (workflow
 *     name). Avoids a new undo entry per keystroke.
 *   • color inputs → live commit via `onUpdateBlock` for immediate
 *     canvas preview. The 300 ms debounce in `useMultiSheetWorkflow`
 *     collapses rapid color-picker drags into a single undo entry.
 */
export function PropertiesPanel({
  selectedBlock, selectedGroup, selectedConnectorId = null,
  workflowName, onUpdateWorkflowName,
  onUpdateBlock, onDeleteBlock, onUpdateGroup, onDeleteGroup,
  onUpdateConnector, onDeleteConnector, onUpdateConnectorWaypoints, onCreateConnector,
  blocks, connectors, groups, blockDimensions,
  canvasWidth, canvasHeight, viewportWidth, viewportHeight,
  scrollLeft, scrollTop, zoom,
  onMinimapNavigate, onNavigateToGroup,
  sheets, activeSheetIndex, crossSheetConnectors,
  onStartCrossSheetLink, onDeleteCrossSheetLink, onNavigateToSheet,
  workflowId = null,
}: PropertiesPanelProps) {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const minimapSearchProps = useMinimapSearchProps(workflowId);
  const [localConfig, setLocalConfig] = useState<BlockConfig | null>(null);
  const [triggerEvent, setTriggerEvent] = useState('user-created');
  const [minimapMode, setMinimapMode] = useState<MinimapMode>('normal');
  const minimapCaps = useMinimapCapabilities();

  // Peer cursors via Supabase Realtime presence. Tier-gated upstream:
  // canShowAdvancedOverlays is the same flag the Minimap uses to render
  // CursorsLayer at all, so non-Max tiers don't subscribe to the channel.
  const peerCursorsSelf = useMemo(() => {
    if (!minimapCaps.canShowAdvancedOverlays) return null;
    if (!user?.id) return null;
    return {
      userId: user.id,
      userName: user.email ?? user.id.slice(0, 8),
      color: '#0ea5e9',
      x: scrollLeft + viewportWidth / 2,
      y: scrollTop + viewportHeight / 2,
    };
  }, [
    minimapCaps.canShowAdvancedOverlays,
    user?.id,
    user?.email,
    scrollLeft,
    scrollTop,
    viewportWidth,
    viewportHeight,
  ]);
  const peerCursors = usePeerCursors({
    workflowId: minimapCaps.canShowAdvancedOverlays ? workflowId : null,
    self: peerCursorsSelf,
  });
  const [localGroupName, setLocalGroupName] = useState('');
  // Local buffer for workflow name so keystrokes don't each push an undo
  // entry. Flushes on blur + Enter. Kept in sync if the parent resets
  // workflowName (e.g. after load).
  const [localWorkflowName, setLocalWorkflowName] = useState(workflowName);
  useEffect(() => { setLocalWorkflowName(workflowName); }, [workflowName]);

  // Resolve the currently selected connector (if any). Mutually exclusive
  // with block/group selection — when present, the panel renders connector
  // properties instead of the block/group form.
  const selectedConnector = useMemo(() => {
    if (!selectedConnectorId) return null;
    return connectors.find(c => c.id === selectedConnectorId) ?? null;
  }, [selectedConnectorId, connectors]);

  // Pre-compute the return-flow port pair so the "Skapa returflöde" button
  // can disable itself when no port combination remains free.
  const connectorReturnPair = useMemo(() => {
    if (!selectedConnector) return undefined;
    const fp = selectedConnector.fromPort || 'right';
    const tp = selectedConnector.toPort || 'left';
    const usedKeys = getUsedPortKeys(connectors, selectedConnector.fromId, selectedConnector.toId);
    return getAvailableReturnPortPair(selectedConnector.fromId, selectedConnector.toId, fp, tp, usedKeys);
  }, [selectedConnector, connectors]);

  useEffect(() => {
    if (selectedBlock) {
      setLocalConfig({ ...selectedBlock.config });
    } else {
      setLocalConfig(null);
    }
  }, [selectedBlock]);

  useEffect(() => {
    if (selectedGroup) {
      setLocalGroupName(selectedGroup.name || '');
    } else {
      setLocalGroupName('');
    }
  }, [selectedGroup]);

  const flushWorkflowName = () => {
    if (localWorkflowName !== workflowName) {
      onUpdateWorkflowName(localWorkflowName);
    }
  };

  const handleSave = () => {
    if (selectedBlock && localConfig) {
      onUpdateBlock(selectedBlock.id, { config: localConfig });
    }
  };

  const handleDelete = () => {
    if (selectedBlock) {
      onDeleteBlock(selectedBlock.id);
    }
  };

  const handleSaveGroup = () => {
    if (selectedGroup) {
      onUpdateGroup(selectedGroup.id, { name: localGroupName });
    }
  };

  const handleDeleteGroup = () => {
    if (selectedGroup) {
      onDeleteGroup(selectedGroup.id);
    }
  };

  const updateLocalConfig = (key: string, value: string) => {
    if (localConfig) {
      setLocalConfig({ ...localConfig, [key]: value });
    }
  };

  return (
    <aside
      className="w-80 flex flex-col h-full overflow-hidden"
      role="complementary"
      aria-label="Properties panel"
      data-testid="properties-panel"
      data-guide="properties"
    >
      <div className="flex-1 overflow-y-auto">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-2 mb-4">
            {selectedConnector ? <Spline className="w-4 h-4 text-muted-foreground" /> : <Settings2 className="w-4 h-4 text-muted-foreground" />}
            <h2 className="font-semibold text-sm">
              {selectedConnector
                ? 'Connector properties'
                : selectedGroup ? t('groupProperties') : t('blockProperties')}
            </h2>
          </div>

          {selectedConnector ? (
            <ConnectorPropertiesSection
              connector={selectedConnector}
              onChange={(patch) => onUpdateConnector?.(selectedConnector.id, patch)}
              onDelete={() => onDeleteConnector?.(selectedConnector.id)}
              onResetRouting={onUpdateConnectorWaypoints
                ? () => onUpdateConnectorWaypoints(selectedConnector.id, [])
                : undefined}
              onCreateReturn={connectorReturnPair && onCreateConnector
                ? () => onCreateConnector(
                    selectedConnector.toId, selectedConnector.fromId,
                    connectorReturnPair.fromPort, connectorReturnPair.toPort,
                  )
                : undefined}
              hasReturn={connectorReturnPair === null}
            />
          ) : selectedGroup ? (
            <GroupPropertiesSection
              selectedGroup={selectedGroup}
              localGroupName={localGroupName}
              onLocalGroupNameChange={setLocalGroupName}
              onUpdateGroup={onUpdateGroup}
              onSave={handleSaveGroup}
              onDelete={handleDeleteGroup}
            />
          ) : selectedBlock && localConfig ? (
            <BlockPropertiesSection
              selectedBlock={selectedBlock}
              localConfig={localConfig}
              updateLocalConfig={updateLocalConfig}
              onUpdateBlock={onUpdateBlock}
              onSave={handleSave}
              onDelete={handleDelete}
              blockNameSuggestionsSlot={
                <BlockNameSuggestions
                  block={selectedBlock}
                  onPick={(name) => updateLocalConfig('name', name)}
                />
              }
              crossSheetLinksSlot={
                /* Cross-sheet links — only rendered when the workspace has
                   2+ sheets (via `sheets` prop) and cross-sheet handlers
                   are wired. Shows existing links for this block and a
                   one-click "start a new link" button that pre-picks this
                   block as the source. */
                sheets && sheets.length > 1 && onStartCrossSheetLink ? (
                  <CrossSheetLinksSection
                    block={selectedBlock}
                    sheets={sheets}
                    activeSheetIndex={activeSheetIndex ?? 0}
                    crossSheetConnectors={crossSheetConnectors ?? []}
                    onStart={onStartCrossSheetLink}
                    onDelete={onDeleteCrossSheetLink}
                    onNavigateToSheet={onNavigateToSheet}
                  />
                ) : null
              }
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('clickToConfig')}
            </p>
          )}
        </div>

        {isAuthenticated && (
          <>
            <TemplateSettingsSection
              triggerEvent={triggerEvent}
              onTriggerEventChange={setTriggerEvent}
              localWorkflowName={localWorkflowName}
              onLocalWorkflowNameChange={setLocalWorkflowName}
              onFlushWorkflowName={flushWorkflowName}
            />

            <OverviewSection
              blocks={blocks}
              connectors={connectors}
              groups={groups}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              viewportWidth={viewportWidth}
              viewportHeight={viewportHeight}
              scrollLeft={scrollLeft}
              scrollTop={scrollTop}
              zoom={zoom}
              blockDimensions={blockDimensions}
              onMinimapNavigate={onMinimapNavigate}
              onNavigateToGroup={onNavigateToGroup}
              canShowAdvancedOverlays={minimapCaps.canShowAdvancedOverlays}
              minimapMode={minimapMode}
              onMinimapModeChange={setMinimapMode}
              minimapSearchProps={minimapSearchProps}
              peerCursors={peerCursors}
            />
          </>
        )}
      </div>
    </aside>
  );
}
